/**
 * SpotifyAuthService
 *
 * Implements the Spotify OAuth 2.0 Authorization Code with PKCE flow
 * (RFC 7636 / Spotify developer docs).
 *
 * ─── Public API ────────────────────────────────────────────────────────────
 *  login()               – generates PKCE material, stores it, redirects to Spotify /authorize
 *  handleCallback()      – validates state, exchanges code for tokens at /api/token
 *  getAccessToken()      – returns a valid access token, refreshing proactively if expired
 *  refreshAccessToken()  – exchanges refresh_token for a new access_token
 *  logout()              – clears all stored Spotify tokens
 *  isAuthenticated()     – true when a non-expired token is stored
 *
 * ─── Storage strategy ──────────────────────────────────────────────────────
 *  sessionStorage: PKCE verifier + state — only needed during the handshake;
 *                  intentionally cleared immediately after token exchange.
 *  localStorage:   access_token, refresh_token, expiry — persist across reloads.
 *
 * ─── Configuration ─────────────────────────────────────────────────────────
 *  Set environment.spotify.clientId, redirectUri, and scopes in each
 *  src/environments/environment*.ts file.
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/** Shape of the JSON response from /api/token */
interface SpotifyTokenResponse {
  access_token:  string;
  token_type:    string;
  expires_in:    number;      // seconds
  refresh_token?: string;     // only present on first login or token rotation
  scope:         string;
}

@Injectable({ providedIn: 'root' })
export class SpotifyAuthService {

  private http = inject(HttpClient);

  // ─── localStorage keys (persist across page reloads) ──────────────────────
  private readonly KEY_ACCESS_TOKEN  = 'spotify_access_token';
  private readonly KEY_REFRESH_TOKEN = 'spotify_refresh_token';
  private readonly KEY_EXPIRY        = 'spotify_token_expiry'; // Unix ms timestamp

  // ─── sessionStorage keys (single-use; cleared after token exchange) ────────
  private readonly SESSION_VERIFIER  = 'spotify_pkce_verifier';
  private readonly SESSION_STATE     = 'spotify_pkce_state';

  // ─── Token buffer: refresh this many ms before actual expiry ──────────────
  private readonly EXPIRY_BUFFER_MS = 60_000; // 1 minute

  // ──────────────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Starts the Spotify Authorization Code + PKCE flow.
   *
   * 1. Generates a cryptographically random code_verifier (96 bytes → base64url)
   * 2. Derives the code_challenge via SHA-256 (RFC 7636 §4.2)
   * 3. Generates a random state string for CSRF protection
   * 4. Persists verifier + state to sessionStorage for use in handleCallback()
   * 5. Redirects the browser to https://accounts.spotify.com/authorize
   */
  async login(): Promise<void> {
    const verifier  = this.generateVerifier();
    const challenge = await this.generateChallenge(verifier);
    const state     = this.generateState();

    sessionStorage.setItem(this.SESSION_VERIFIER, verifier);
    sessionStorage.setItem(this.SESSION_STATE, state);

    const params = new URLSearchParams({
      client_id:             environment.spotify.clientId,
      response_type:         'code',
      redirect_uri:          environment.spotify.redirectUri,
      scope:                 environment.spotify.scopes.join(' '),
      state,
      code_challenge_method: 'S256',
      code_challenge:        challenge,
    });

    window.location.href = `https://accounts.spotify.com/authorize?${params.toString()}`;
  }

  /**
   * Called by SpotifyCallbackPage after Spotify redirects back with ?code=&state=.
   *
   * 1. Validates the returned state against the stored value (CSRF check)
   * 2. Immediately clears sessionStorage (verifier + state are single-use)
   * 3. POSTs to /api/token with grant_type=authorization_code + code_verifier
   * 4. Stores the returned access_token, refresh_token, and expiry
   *
   * @throws Error if state mismatches or the token exchange request fails.
   */
  async handleCallback(code: string, state: string): Promise<void> {
    const storedState = sessionStorage.getItem(this.SESSION_STATE);
    const verifier    = sessionStorage.getItem(this.SESSION_VERIFIER);

    // CSRF protection: the state returned by Spotify must match what we sent
    if (!storedState || storedState !== state) {
      throw new Error('State mismatch — possible CSRF attack. Please try logging in again.');
    }
    if (!verifier) {
      throw new Error('PKCE verifier not found. The session may have expired; please try again.');
    }

    // Single-use — clear immediately before the network call
    sessionStorage.removeItem(this.SESSION_STATE);
    sessionStorage.removeItem(this.SESSION_VERIFIER);

    const body = new URLSearchParams({
      grant_type:    'authorization_code',
      code,
      redirect_uri:  environment.spotify.redirectUri,
      client_id:     environment.spotify.clientId,
      code_verifier: verifier,
    });

    const response = await firstValueFrom(
      this.http.post<SpotifyTokenResponse>(
        'https://accounts.spotify.com/api/token',
        body.toString(),
        { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
      )
    );

    this.storeTokens(response);
  }

  /**
   * Returns a valid access token, refreshing it first if it has expired
   * (or is within the 60-second buffer window).
   *
   * Returns null if the user has never authenticated.
   */
  async getAccessToken(): Promise<string | null> {
    const stored = this.getStoredAccessToken();
    if (!stored) return null;

    if (this.isTokenExpired()) {
      return this.refreshAccessToken();
    }

    return stored;
  }

  /**
   * Exchanges the stored refresh_token for a new access_token.
   *
   * Spotify may return a new refresh_token (token rotation); if so it
   * replaces the old one.  If the request fails, tokens are cleared and
   * null is returned (the user must log in again).
   */
  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem(this.KEY_REFRESH_TOKEN);
    if (!refreshToken) return null;

    try {
      const body = new URLSearchParams({
        grant_type:    'refresh_token',
        refresh_token: refreshToken,
        client_id:     environment.spotify.clientId,
      });

      const response = await firstValueFrom(
        this.http.post<SpotifyTokenResponse>(
          'https://accounts.spotify.com/api/token',
          body.toString(),
          { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
        )
      );

      this.storeTokens(response);
      return response.access_token;
    } catch (err) {
      console.error('[SpotifyAuthService] Token refresh failed:', err);
      // Refresh token is invalid or revoked; force the user to re-authenticate
      this.logout();
      return null;
    }
  }

  /**
   * Removes all Spotify tokens from localStorage.
   * Does not affect Firebase authentication.
   */
  logout(): void {
    localStorage.removeItem(this.KEY_ACCESS_TOKEN);
    localStorage.removeItem(this.KEY_REFRESH_TOKEN);
    localStorage.removeItem(this.KEY_EXPIRY);
  }

  /**
   * Returns true if an access token is stored AND has not yet expired
   * (accounting for the 60-second buffer).
   */
  isAuthenticated(): boolean {
    return !!this.getStoredAccessToken() && !this.isTokenExpired();
  }

  /**
   * Synchronously returns the raw stored access_token without checking expiry.
   * Used by SpotifyAuthInterceptor for the initial request attempt.
   */
  getStoredAccessToken(): string | null {
    return localStorage.getItem(this.KEY_ACCESS_TOKEN);
  }

  /**
   * Returns true when the stored token is expired (or missing), factoring in
   * the 60-second buffer so we refresh proactively before hard expiry.
   */
  isTokenExpired(): boolean {
    const expiry = localStorage.getItem(this.KEY_EXPIRY);
    if (!expiry) return true;
    return Date.now() > parseInt(expiry, 10) - this.EXPIRY_BUFFER_MS;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ──────────────────────────────────────────────────────────────────────────

  /** Persists tokens from /api/token, preserving existing refresh_token if none returned. */
  private storeTokens(response: SpotifyTokenResponse): void {
    localStorage.setItem(this.KEY_ACCESS_TOKEN, response.access_token);

    // refresh_token is only included on first authorization or when Spotify rotates it.
    // Keep the existing one when Spotify doesn't send a replacement.
    if (response.refresh_token) {
      localStorage.setItem(this.KEY_REFRESH_TOKEN, response.refresh_token);
    }

    const expiresAt = Date.now() + response.expires_in * 1000;
    localStorage.setItem(this.KEY_EXPIRY, expiresAt.toString());
  }

  /**
   * Generates a PKCE code_verifier: 96 random bytes encoded as base64url.
   * Length ≈ 128 characters (well within the 43–128 char spec limit).
   */
  private generateVerifier(): string {
    const array = new Uint8Array(96);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Derives the PKCE code_challenge from the verifier:
   * BASE64URL( SHA-256( ASCII(code_verifier) ) )  — RFC 7636 §4.2
   */
  private async generateChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data    = encoder.encode(verifier);
    const digest  = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Generates a random 32-character hex string for the OAuth state parameter
   * (CSRF protection, RFC 6749 §10.12).
   */
  private generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }
}
